import { useState, useEffect, useCallback } from "react";
import { useLang } from "@/context/LanguageContext";
import { X, ArrowRight, ArrowLeft, Check, CheckCircle2, Home, Image, BedDouble, CalendarCheck, Phone } from "lucide-react";

const API_URL = "https://staysy-app.legiroweb.site/api/onboarding";

// ─── Theme preview mockups ────────────────────────────────────────────────────

function ThemeMockup({ themeId }) {
  const themes = {
    alpine: {
      nav: "bg-stone-800",
      navText: "text-stone-100",
      bg: "bg-stone-50",
      hero: "bg-stone-200",
      heroOverlay: "bg-gradient-to-br from-stone-300/60 to-stone-400/40",
      card: "bg-white border border-stone-200",
      cardImg: "bg-stone-200",
      accent: "bg-emerald-600",
      radius: "rounded",
      heroRadius: "rounded-xl",
    },
    luxury: {
      nav: "bg-zinc-950",
      navText: "text-zinc-100",
      bg: "bg-zinc-900",
      hero: "bg-zinc-800",
      heroOverlay: "bg-gradient-to-br from-yellow-900/30 to-zinc-800/60",
      card: "bg-zinc-800 border border-yellow-800/40",
      cardImg: "bg-zinc-700",
      accent: "bg-yellow-500",
      radius: "rounded-sm",
      heroRadius: "rounded",
    },
    cottage: {
      nav: "bg-amber-900",
      navText: "text-amber-50",
      bg: "bg-amber-50",
      hero: "bg-amber-200",
      heroOverlay: "bg-gradient-to-br from-amber-300/50 to-orange-200/40",
      card: "bg-white border border-amber-200",
      cardImg: "bg-amber-200",
      accent: "bg-amber-600",
      radius: "rounded-xl",
      heroRadius: "rounded-2xl",
    },
    minimal: {
      nav: "bg-white border-b border-zinc-200",
      navText: "text-zinc-900",
      bg: "bg-zinc-50",
      hero: "bg-zinc-200",
      heroOverlay: "bg-gradient-to-br from-zinc-200/50 to-zinc-300/30",
      card: "bg-white border border-zinc-200",
      cardImg: "bg-zinc-200",
      accent: "bg-zinc-900",
      radius: "rounded",
      heroRadius: "rounded-lg",
    },
    seaside: {
      nav: "bg-sky-900",
      navText: "text-sky-50",
      bg: "bg-sky-50",
      hero: "bg-sky-200",
      heroOverlay: "bg-gradient-to-br from-sky-300/50 to-blue-200/40",
      card: "bg-white border border-sky-200",
      cardImg: "bg-sky-200",
      accent: "bg-sky-600",
      radius: "rounded-xl",
      heroRadius: "rounded-2xl",
    },
  };

  const t = themes[themeId] || themes.alpine;

  return (
    <div className={`w-full h-full ${t.bg} overflow-hidden flex flex-col`}>
      {/* Nav */}
      <div className={`${t.nav} px-2 py-1.5 flex items-center justify-between`}>
        <div className={`text-[5px] font-bold ${t.navText} tracking-wide`}>PENZION</div>
        <div className="flex items-center gap-1">
          <div className={`text-[4px] ${t.navText} opacity-70`}>Pokoje</div>
          <div className={`text-[4px] ${t.navText} opacity-70`}>Galerie</div>
          <div className={`${t.accent} text-white text-[4px] px-1 py-0.5 ${t.radius}`}>
            Rezervovat
          </div>
        </div>
      </div>

      {/* Hero photo frame */}
      <div className="px-2 pt-2">
        <div className={`${t.hero} ${t.heroRadius} relative overflow-hidden`} style={{ height: 52 }}>
          <div className={`absolute inset-0 ${t.heroOverlay}`} />
          {/* Photo grid lines to suggest a photo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="opacity-20">
              <div className={`w-8 h-6 ${t.cardImg} ${t.radius} border border-white/30`} />
            </div>
          </div>
          <div className="absolute bottom-1.5 left-2 right-2">
            <div className={`${t.navText === "text-zinc-100" || t.navText === "text-sky-50" || t.navText === "text-amber-50" || t.navText === "text-stone-100" ? "text-white" : "text-white"} text-[5px] font-bold`} style={{ textShadow: '0 1px 3px rgba(0,0,0,.5)' }}>
              Vítejte v našem ubytování
            </div>
          </div>
        </div>
      </div>

      {/* Room cards */}
      <div className="px-2 pt-1.5 pb-2 grid grid-cols-3 gap-1">
        {[0, 1, 2].map(i => (
          <div key={i} className={`${t.card} ${t.radius} overflow-hidden`}>
            <div className={`${t.cardImg} w-full`} style={{ height: 20 }} />
            <div className="p-0.5">
              <div className={`text-[4px] font-semibold ${t.navText === "text-zinc-100" ? "text-zinc-300" : "text-gray-700"}`}>Pokoj {i + 1}</div>
              <div className={`text-[3.5px] ${t.navText === "text-zinc-100" ? "text-zinc-500" : "text-gray-400"}`}>od 1 200 Kč</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Theme data ───────────────────────────────────────────────────────────────

const THEMES = [
  {
    id: "alpine",
    name: "Alpine",
    tagline: "Příroda & hory",
    palette: ["#44403C", "#ECFDF5", "#059669", "#F5F5F4"],
  },
  {
    id: "luxury",
    name: "Luxury",
    tagline: "Elegance & tmavý styl",
    palette: ["#09090B", "#18181B", "#CA8A04", "#3F3F46"],
  },
  {
    id: "cottage",
    name: "Cottage",
    tagline: "Teplo & pohoda",
    palette: ["#78350F", "#FFFBEB", "#D97706", "#FEF3C7"],
  },
  {
    id: "minimal",
    name: "Minimal",
    tagline: "Čistota & přehlednost",
    palette: ["#FFFFFF", "#F4F4F5", "#18181B", "#E4E4E7"],
  },
  {
    id: "seaside",
    name: "Seaside",
    tagline: "Moře & svěžest",
    palette: ["#0C4A6E", "#F0F9FF", "#0284C7", "#E0F2FE"],
  },
];

// ─── Page options ─────────────────────────────────────────────────────────────

const PAGES = [
  {
    id: "home",
    icon: Home,
    title: "Domovská stránka",
    desc: "Uvítání, foto hero, popis ubytování",
    required: true,
  },
  {
    id: "rooms",
    icon: BedDouble,
    title: "Pokoje & apartmány",
    desc: "Přehled pokojů s fotkami a cenami",
  },
  {
    id: "gallery",
    icon: Image,
    title: "Galerie fotek",
    desc: "Plnoobrazová galerie ubytování",
  },
  {
    id: "reservation",
    icon: CalendarCheck,
    title: "Online rezervace",
    desc: "Formulář přímé rezervace bez provize",
  },
  {
    id: "contact",
    icon: Phone,
    title: "Kontakt & mapa",
    desc: "Kontaktní údaje, Google mapa, formulář",
  },
];

// ─── Main modal ───────────────────────────────────────────────────────────────

const TOTAL_STEPS = 4;

export default function OnboardingModal({ open, onClose, prefillEmail = "" }) {
  const { lang } = useLang();
  const cs = lang === "cs";

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    accommodation_name: "",
    owner_name: "",
    email: prefillEmail,
    phone: "",
    theme: "alpine",
    pages: ["home", "reservation", "contact"],
    note: "",
  });

  const [touched, setTouched] = useState({});

  // Pre-fill email when it changes from outside
  useEffect(() => {
    if (prefillEmail) {
      setForm((f) => ({ ...f, email: prefillEmail }));
    }
  }, [prefillEmail]);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setSubmitted(false);
      setError("");
      setTouched({});
    }
  }, [open]);

  // Close on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Prevent scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setTouched((t) => ({ ...t, [field]: true }));
  };

  const togglePage = (id) => {
    if (id === "home") return; // required
    setForm((f) => ({
      ...f,
      pages: f.pages.includes(id)
        ? f.pages.filter((p) => p !== id)
        : [...f.pages, id],
    }));
  };

  // Step 1 validation
  const step1Valid =
    form.accommodation_name.trim().length >= 2 &&
    form.owner_name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const canNext = () => {
    if (step === 1) return step1Valid;
    if (step === 2) return !!form.theme;
    if (step === 3) return form.pages.length >= 1;
    return true;
  };

  const next = () => {
    if (step < TOTAL_STEPS && canNext()) setStep((s) => s + 1);
  };
  const prev = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Chyba serveru");
      setSubmitted(true);
    } catch (e) {
      setError(
        cs
          ? "Nepodařilo se odeslat poptávku. Zkuste to prosím znovu."
          : "Failed to submit. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const stepLabels = cs
    ? ["O ubytování", "Design", "Stránky", "Souhrn"]
    : ["About", "Design", "Pages", "Summary"];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div className="relative bg-[#FDFBF7] w-full sm:max-w-2xl max-h-[96dvh] sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#D1FAE5] flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#047857]/60 mb-0.5">
              {cs ? `Krok ${step} z ${TOTAL_STEPS}` : `Step ${step} of ${TOTAL_STEPS}`}
            </div>
            <h2 className="font-heading text-xl font-black text-[#064E3B]">
              {stepLabels[step - 1]}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#D1FAE5] flex items-center justify-center text-[#047857] hover:bg-[#A7F3D0] transition-colors"
            aria-label="Zavřít"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1 bg-[#D1FAE5] flex-shrink-0">
          <div
            className="h-full bg-[#22C55E] transition-all duration-500 ease-out"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {submitted ? (
            <SuccessView cs={cs} onClose={onClose} />
          ) : (
            <>
              {step === 1 && <Step1 form={form} setField={setField} cs={cs} touched={touched} />}
              {step === 2 && (
                <Step2 selectedTheme={form.theme} onSelect={(t) => setField("theme", t)} cs={cs} />
              )}
              {step === 3 && (
                <Step3 pages={form.pages} onToggle={togglePage} cs={cs} />
              )}
              {step === 4 && (
                <Step4
                  form={form}
                  cs={cs}
                  note={form.note}
                  onNoteChange={(v) => setField("note", v)}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="flex-shrink-0 px-6 py-5 border-t border-[#D1FAE5] flex items-center justify-between gap-3">
            {step > 1 ? (
              <button
                onClick={prev}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-[#D1FAE5] text-[#047857] font-body font-semibold text-sm hover:bg-[#D1FAE5]/50 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                {cs ? "Zpět" : "Back"}
              </button>
            ) : (
              <div />
            )}

            {error && (
              <p className="text-xs text-red-600 flex-1 text-center">{error}</p>
            )}

            {step < TOTAL_STEPS ? (
              <button
                onClick={next}
                disabled={!canNext()}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#064E3B] text-white font-body font-bold text-sm hover:bg-[#22C55E] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {cs ? "Pokračovat" : "Continue"}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={loading}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#22C55E] text-white font-body font-bold text-sm shadow-lg shadow-green-900/20 hover:bg-[#16A34A] hover:scale-105 transition-all disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    {cs ? "Odesílám…" : "Sending…"}
                  </span>
                ) : (
                  <>
                    {cs ? "Odeslat poptávku" : "Submit enquiry"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 1 ───────────────────────────────────────────────────────────────────

function Step1({ form, setField, cs, touched }) {
  const fields = [
    {
      key: "accommodation_name",
      label: cs ? "Název ubytování" : "Accommodation name",
      placeholder: cs ? "Penzion Na Kopci" : "Mountain View Lodge",
      required: true,
      type: "text",
    },
    {
      key: "owner_name",
      label: cs ? "Vaše jméno" : "Your name",
      placeholder: cs ? "Jan Novák" : "John Smith",
      required: true,
      type: "text",
    },
    {
      key: "email",
      label: cs ? "E-mailová adresa" : "Email address",
      placeholder: "jan@penzion.cz",
      required: true,
      type: "email",
    },
    {
      key: "phone",
      label: cs ? "Telefon (nepovinné)" : "Phone (optional)",
      placeholder: "+420 777 000 000",
      required: false,
      type: "tel",
    },
  ];

  return (
    <div>
      <p className="font-body text-sm text-[#047857] mb-6">
        {cs
          ? "Začneme od základu — jak se jmenuje vaše ubytování a jak vás máme kontaktovat?"
          : "Let's start with the basics — what is your accommodation called and how can we reach you?"}
      </p>
      <div className="space-y-4">
        {fields.map((f) => {
          const isInvalid =
            touched[f.key] &&
            f.required &&
            !form[f.key]?.trim();
          const isEmailInvalid =
            touched.email &&
            f.key === "email" &&
            form.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

          return (
            <div key={f.key}>
              <label className="block font-body text-xs font-bold text-[#064E3B] mb-1.5 uppercase tracking-wide">
                {f.label}
                {f.required && <span className="text-[#22C55E] ml-0.5">*</span>}
              </label>
              <input
                type={f.type}
                value={form[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                className={`w-full h-11 px-4 rounded-xl border font-body text-sm text-[#064E3B] placeholder:text-[#047857]/40 bg-white outline-none transition-colors
                  ${isInvalid || isEmailInvalid
                    ? "border-red-400 focus:border-red-500"
                    : "border-[#D1FAE5] focus:border-[#22C55E]"
                  }`}
              />
              {(isInvalid) && (
                <p className="text-xs text-red-500 mt-1">
                  {cs ? "Toto pole je povinné." : "This field is required."}
                </p>
              )}
              {isEmailInvalid && (
                <p className="text-xs text-red-500 mt-1">
                  {cs ? "Zadejte platný e-mail." : "Enter a valid email."}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 2 ───────────────────────────────────────────────────────────────────

function Step2({ selectedTheme, onSelect, cs }) {
  return (
    <div>
      <p className="font-body text-sm text-[#047857] mb-6">
        {cs
          ? "Každý design je připravený speciálně pro ubytování. Finální vzhled doladíme podle vás."
          : "Each design is crafted for accommodation businesses. We'll fine-tune it with your feedback."}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {THEMES.map((theme) => {
          const selected = selectedTheme === theme.id;
          return (
            <button
              key={theme.id}
              onClick={() => onSelect(theme.id)}
              className={`group relative flex flex-col rounded-2xl overflow-hidden border-2 transition-all duration-200 text-left
                ${selected
                  ? "border-[#22C55E] shadow-lg shadow-green-900/15 scale-[1.02]"
                  : "border-[#D1FAE5] hover:border-[#86EFAC] hover:shadow-md"
                }`}
            >
              {/* Selected check */}
              {selected && (
                <div className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-[#22C55E] flex items-center justify-center shadow">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}

              {/* Mini website mockup */}
              <div
                className="w-full overflow-hidden"
                style={{ height: 140 }}
              >
                <ThemeMockup themeId={theme.id} />
              </div>

              {/* Color palette dots */}
              <div className="px-3 pt-2 pb-1 flex items-center gap-1">
                {theme.palette.map((color, i) => (
                  <span
                    key={i}
                    className="w-3 h-3 rounded-full border border-black/10 flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Info */}
              <div className="px-3 pb-3">
                <div className="font-heading text-sm font-black text-[#064E3B]">
                  {theme.name}
                </div>
                <div className="font-body text-[11px] text-[#047857] mt-0.5">
                  {theme.tagline}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 3 ───────────────────────────────────────────────────────────────────

function Step3({ pages, onToggle, cs }) {
  return (
    <div>
      <p className="font-body text-sm text-[#047857] mb-6">
        {cs
          ? "Vyberte, jaké sekce váš web bude mít. Domovská stránka je vždy součástí."
          : "Select which sections your website will have. The home page is always included."}
      </p>
      <div className="space-y-2.5">
        {PAGES.map((page) => {
          const active = pages.includes(page.id);
          const Icon = page.icon;
          return (
            <button
              key={page.id}
              onClick={() => onToggle(page.id)}
              disabled={page.required}
              className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border-2 transition-all text-left
                ${active
                  ? "border-[#22C55E] bg-[#ECFDF5]"
                  : "border-[#D1FAE5] bg-white hover:border-[#86EFAC]"
                }
                ${page.required ? "cursor-default opacity-90" : "cursor-pointer"}`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
                ${active ? "bg-[#22C55E]" : "bg-[#D1FAE5]"}`}>
                <Icon className={`w-4.5 h-4.5 ${active ? "text-white" : "text-[#047857]"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-body text-sm font-bold text-[#064E3B] flex items-center gap-2">
                  {page.title}
                  {page.required && (
                    <span className="text-[10px] font-normal text-[#047857] bg-[#D1FAE5] px-1.5 py-0.5 rounded-full">
                      {cs ? "vždy" : "always"}
                    </span>
                  )}
                </div>
                <div className="font-body text-xs text-[#047857] mt-0.5">{page.desc}</div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                ${active ? "border-[#22C55E] bg-[#22C55E]" : "border-[#D1FAE5]"}`}>
                {active && <Check className="w-3 h-3 text-white" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 4 ───────────────────────────────────────────────────────────────────

function Step4({ form, cs, note, onNoteChange }) {
  const selectedTheme = THEMES.find((t) => t.id === form.theme);
  const selectedPages = PAGES.filter((p) => form.pages.includes(p.id));

  return (
    <div>
      <p className="font-body text-sm text-[#047857] mb-6">
        {cs
          ? "Zkontrolujte výběr a přidejte případnou poznámku. Ozveme se do 24 hodin."
          : "Review your selection and add any notes. We'll be in touch within 24 hours."}
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        {/* Theme preview */}
        <div className="rounded-2xl border-2 border-[#D1FAE5] overflow-hidden">
          <div className="overflow-hidden" style={{ height: 120 }}>
            <ThemeMockup themeId={form.theme} />
          </div>
          <div className="px-3 py-2.5 bg-[#ECFDF5]">
            <div className="text-[10px] uppercase tracking-widest text-[#047857]/60 font-bold mb-0.5">
              {cs ? "Zvolený design" : "Selected design"}
            </div>
            <div className="font-heading text-sm font-black text-[#064E3B]">
              {selectedTheme?.name}
            </div>
            <div className="flex gap-1 mt-1">
              {selectedTheme?.palette.map((c, i) => (
                <span key={i} className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>

        {/* Info summary */}
        <div className="space-y-3">
          <SummaryRow label={cs ? "Ubytování" : "Accommodation"} value={form.accommodation_name} />
          <SummaryRow label={cs ? "Kontakt" : "Contact"} value={`${form.owner_name} · ${form.email}`} />
          {form.phone && <SummaryRow label={cs ? "Telefon" : "Phone"} value={form.phone} />}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#047857]/60 font-bold mb-1.5">
              {cs ? "Stránky" : "Pages"}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selectedPages.map((p) => (
                <span key={p.id} className="text-xs px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#064E3B] font-medium">
                  {p.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Note */}
      <div>
        <label className="block font-body text-xs font-bold text-[#064E3B] mb-1.5 uppercase tracking-wide">
          {cs ? "Poznámka (nepovinné)" : "Notes (optional)"}
        </label>
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder={cs
            ? "Zvláštní požadavky, otázky nebo informace, které bychom měli vědět…"
            : "Special requirements, questions, or anything we should know…"}
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-[#D1FAE5] focus:border-[#22C55E] font-body text-sm text-[#064E3B] placeholder:text-[#047857]/40 bg-white outline-none resize-none transition-colors"
        />
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#047857]/60 font-bold mb-0.5">
        {label}
      </div>
      <div className="font-body text-sm font-medium text-[#064E3B] break-all">{value}</div>
    </div>
  );
}

// ─── Success ──────────────────────────────────────────────────────────────────

function SuccessView({ cs, onClose }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="w-20 h-20 rounded-full bg-[#ECFDF5] flex items-center justify-center mb-6">
        <CheckCircle2 className="w-10 h-10 text-[#22C55E]" />
      </div>
      <h3 className="font-heading text-2xl font-black text-[#064E3B] mb-3">
        {cs ? "Poptávka odeslána!" : "Enquiry submitted!"}
      </h3>
      <p className="font-body text-base text-[#047857] max-w-sm">
        {cs
          ? "Děkujeme! Ozveme se vám do 24 hodin. Váš web připravíme do 3 pracovních dnů."
          : "Thank you! We'll be in touch within 24 hours. Your website will be ready in 3 business days."}
      </p>
      <div className="mt-8 flex flex-col gap-2 w-full max-w-xs">
        <button
          onClick={onClose}
          className="h-12 rounded-full bg-[#064E3B] text-white font-body font-bold text-sm hover:bg-[#22C55E] transition-all"
        >
          {cs ? "Zavřít" : "Close"}
        </button>
      </div>
    </div>
  );
}
